import { Observable, interval, map, startWith } from 'rxjs';

/**
 * These are the available cookies in the application. Add more as needed.
 * Note: Cookies are sent to the server with every request, so they should be used sparingly.
 *
 * Available Cookie Names:
 * - 'trimalias': Used for storing the alias of the user we are impersonating.
 * - 'tenantadmin': Used for storing a flag indicating if the user is a tenant admin.
 * - 'devModeEnable': Used for enabling developer mode.
 */
type CookieName = 'trimalias' | 'tenantadmin' | 'devModeEnable';

export function clearCookie(name: CookieName): void {
  window.document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/; SameSite=Lax; Secure`;
}

export function setCookie(name: CookieName, value: string): void {
  window.document.cookie = `${name}=${value}; path=/; SameSite=Lax; Secure`;
}

export function getCookie(name: CookieName): UNDEFINED<string> {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }
  return undefined;
}

/**
 * Watches for cookies on an interval.
 * @param name the cookie to get
 * @param period the interval period in milliseconds (default is 1000ms)
 * @returns a non-completing observable that emits the cookie value at each interval
 */
export function watchCookie$(name: CookieName, period = 1000): Observable<string | undefined> {
  return interval(period).pipe(
    map(() => getCookie(name)),
    startWith(getCookie(name)),
  );
}
